import { useApolloClient } from "@apollo/client";
import { Button, ButtonGroup, NumericInput } from "@blueprintjs/core";
import { type Post } from "@package/ui-gql/index-data-access";
import {
  SCREEN_DIMENSIONS_BY_RESOLUTION_ORIENTATION,
  WallContext,
} from "@package/ui-wall";
import { type SliceWallScreenState } from "@package/ui-wall-engine";
import { FlyoutTrivia } from "@package/ui-wall-flyouts";
import { useEffect, useMemo, useRef, useState } from "react";
import { WallProviderPreview } from "./wall-provider-preview";

type PreviewMode = "portrait" | "landscape" | "custom";

// The preview grows to fill whatever width its column actually has (measured, not assumed) —
// this height is the only hard cap, so it stays clear of the page's own scroll/toolbar chrome.
const PREVIEW_BOX_MAX_HEIGHT = 640;
const PREVIEW_BOX_FALLBACK_WIDTH = 320;

// Trivia's own type scale is container-query (cqmin) driven, sized to look right at a real
// wall's actual pixel dimensions — the same real FHD sizes the wall app itself uses. Rendering
// the preview at these dimensions and then visually shrinking the whole box with CSS `zoom`
// (the same technique AutoZoomFrame already uses for wall previews elsewhere in the CMS) is
// what makes proportions match a real device instead of being recomputed against a tiny box.
const PREVIEW_MODE_SIZE: Record<
  Exclude<PreviewMode, "custom">,
  { width: number; height: number }
> = {
  portrait: SCREEN_DIMENSIONS_BY_RESOLUTION_ORIENTATION.FHD.portrait,
  landscape: SCREEN_DIMENSIONS_BY_RESOLUTION_ORIENTATION.FHD.landscape,
};

// A wide, short strip is a common single-row LED video wall shape — deliberately not 16:9, to
// make it obvious the custom fields aren't just another orientation preset.
const DEFAULT_CUSTOM_SIZE = { width: 1920, height: 540 };

function getZoomToFit(
  realWidth: number,
  realHeight: number,
  maxWidth: number,
  maxHeight: number,
): number {
  if (realWidth <= 0 || realHeight <= 0 || maxWidth <= 0) {
    return 1;
  }

  return Math.min(maxWidth / realWidth, maxHeight / realHeight, 1);
}

function useMeasuredWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(function effectOnPreviewColumnResized() {
    const element = ref.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured) {
        setWidth(measured);
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}

type PostTriviaPreviewProps = {
  post: Post;
};

export function PostTriviaPreview({ post }: PostTriviaPreviewProps) {
  const client = useApolloClient();
  const [mode, setMode] = useState<PreviewMode>("portrait");
  const [customSize, setCustomSize] = useState(DEFAULT_CUSTOM_SIZE);
  const [restartKey, setRestartKey] = useState(0);
  const [columnRef, columnWidth] = useMeasuredWidth(PREVIEW_BOX_FALLBACK_WIDTH);

  const provider = useMemo(() => new WallProviderPreview(client), [client]);

  const screen = useMemo<SliceWallScreenState>(
    () => ({
      id: "preview",
      active: true,
      index: 0,
      layout: { id: "preview-layout" } as SliceWallScreenState["layout"],
      layoutPostPositions: [],
      menu: false,
      flyout: { post, relatedPosts: [] },
    }),
    [post],
  );

  const realSize = mode === "custom" ? customSize : PREVIEW_MODE_SIZE[mode];
  const zoom = getZoomToFit(
    realSize.width,
    realSize.height,
    columnWidth,
    PREVIEW_BOX_MAX_HEIGHT,
  );

  return (
    <div ref={columnRef} className="flex w-full flex-col items-center gap-3">
      <div className="flex w-full items-center justify-between">
        <span className="bp6-heading text-sm">Preview</span>
        <ButtonGroup>
          <Button
            small
            active={mode === "portrait"}
            text="Portrait"
            onClick={() => setMode("portrait")}
          />
          <Button
            small
            active={mode === "landscape"}
            text="Landscape"
            onClick={() => setMode("landscape")}
          />
          <Button
            small
            active={mode === "custom"}
            text="Custom"
            onClick={() => setMode("custom")}
          />
        </ButtonGroup>
      </div>
      {mode === "custom" && (
        <div className="flex w-full items-center gap-2">
          <NumericInput
            small
            fill
            min={1}
            placeholder="Width (px)"
            value={customSize.width}
            onValueChange={(width) =>
              setCustomSize((prev) => ({ ...prev, width: width || prev.width }))
            }
          />
          <span className="text-brand-muted">×</span>
          <NumericInput
            small
            fill
            min={1}
            placeholder="Height (px)"
            value={customSize.height}
            onValueChange={(height) =>
              setCustomSize((prev) => ({
                ...prev,
                height: height || prev.height,
              }))
            }
          />
        </div>
      )}
      <div
        className="overflow-hidden rounded-lg border border-brand-line shadow-sm"
        style={{
          width: realSize.width,
          height: realSize.height,
          zoom,
        }}
      >
        <WallContext.Provider value={provider}>
          <FlyoutTrivia key={restartKey} screen={screen} disableAnalytics />
        </WallContext.Provider>
      </div>
      <Button
        minimal
        small
        icon="refresh"
        text="Restart Preview"
        onClick={() => setRestartKey((key) => key + 1)}
      />
    </div>
  );
}
